import { useEffect, useMemo, useRef, useState } from 'react';

import { doc, onSnapshot } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';

export interface ExecutionStateRef {
  /** Execution id; `execution_states` documents are keyed by it. */
  id: string;
  divisionId: string;
}

export interface ExecutionStateDoc {
  currentState?: string | null;
  [key: string]: unknown;
}

export type ExecutionStatesByExecutionId = Record<string, ExecutionStateDoc | null>;

/** Drops unaddressable refs and dedupes, sorted so the subscription key is order-independent. */
const normalizeRefs = (refs: ExecutionStateRef[]): ExecutionStateRef[] => {
  const byId = new Map<string, ExecutionStateRef>();

  refs.forEach(({ id, divisionId }) => {
    if (!id || !divisionId || byId.has(id)) return;
    byId.set(id, { id, divisionId });
  });

  return Array.from(byId.values()).sort((a, b) => a.id.localeCompare(b.id));
};

/**
 * Subscribes to `organizations/{orgId}/divisions/{divId}/execution_states/{executionId}` in
 * realtime for a known set of executions.
 *
 * One listener per document is deliberate, not an oversight: the security rules expose these
 * documents with `allow get` but set `allow list: if false`, so any collection-level query —
 * including `where(documentId(), 'in', [...])` — is rejected outright. Batching these reads
 * requires the Admin SDK, i.e. a backend endpoint. Keep the caller's execution set bounded.
 *
 * `loaded` stays false until every listener has reported once, so callers can hold their
 * loading indicator instead of rendering rows before their state resolves.
 */
export const useExecutionStatesRt = (
  organizationId: string | undefined,
  executionRefs: ExecutionStateRef[],
) => {
  const [statesByExecutionId, setStatesByExecutionId] = useState<ExecutionStatesByExecutionId>({});
  const [loaded, setLoaded] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refs = useMemo(() => normalizeRefs(executionRefs), [executionRefs]);

  /** Resubscribe only when the addressed documents change, not on every parent render. */
  const subscriptionKey = useMemo(
    () => `${organizationId ?? ''}|${refs.map(({ id, divisionId }) => `${divisionId}:${id}`).join(',')}`,
    [organizationId, refs],
  );

  const refsRef = useRef(refs);
  refsRef.current = refs;

  useEffect(() => {
    const currentRefs = refsRef.current;

    if (!organizationId || !currentRefs.length) {
      setStatesByExecutionId({});
      setLoaded(true);
      setError(null);
      return undefined;
    }

    const pending = new Set(currentRefs.map(({ id }) => id));
    setLoaded(false);
    setError(null);

    /**
     * Drop the executions this subscription no longer addresses. Merging blindly would
     * leave a stale `currentState` behind, and `undefined` is what tells the caller
     * "not resolved yet": a row that leaves the set and comes back would read as
     * already resolved and be filtered against the state it had last time.
     */
    setStatesByExecutionId(previous => {
      const retained: ExecutionStatesByExecutionId = {};
      currentRefs.forEach(({ id }) => {
        if (previous[id] !== undefined) retained[id] = previous[id];
      });
      return retained;
    });

    const settle = (executionId: string) => {
      pending.delete(executionId);
      if (!pending.size) setLoaded(true);
    };

    const unsubscribes = currentRefs.map(({ id, divisionId }) =>
      onSnapshot(
        doc(
          db,
          FSCollections.ORGANIZATIONS,
          organizationId,
          FSCollections.DIVISIONS,
          divisionId,
          FSCollections.STATE,
          id,
        ),
        snapshot => {
          setStatesByExecutionId(previous => ({
            ...previous,
            // A missing document resolves to null rather than undefined, so callers can tell
            // "not loaded yet" from "has no state".
            [id]: snapshot.exists() ? (snapshot.data() as ExecutionStateDoc) : null,
          }));
          settle(id);
        },
        listenerError => {
          // Surfaced so a denied read cannot silently look like "no rows match the filter".
          setError(listenerError as Error);
          setStatesByExecutionId(previous => ({ ...previous, [id]: null }));
          settle(id);
        },
      ),
    );

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [organizationId, subscriptionKey]);

  return { statesByExecutionId, loaded, error };
};

export default useExecutionStatesRt;
